import { apiService } from "api/apiService";
import { applyHelpfulnessVote } from "composables/knowledge-base/helpfulnessVote";
import type {
  CategoryWithStubs,
  KnowledgeBaseCategory,
  KnowledgeBaseCategoryPayload,
  KnowledgeBaseItem,
  KnowledgeBaseItemCreate,
  KnowledgeBaseItemUpdate,
} from "models/knowledge-base/knowledge-base";
import type {
  KnowledgeBaseLayoutPayloadNode,
  KnowledgeBaseTreeApiCategory,
} from "models/knowledge-base/knowledge-base-tree.types";
import { defineStore } from "pinia";
import { useQuasar } from "quasar";
import { computed, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { useNotifyStore } from "./notifyStore";

export const useKnowledgeBaseStore = defineStore("knowledgeBase", () => {
  // State
  const byId = reactive(new Map<string, KnowledgeBaseItem>());
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const activeItem = ref<KnowledgeBaseItem | null>(null);
  const categories = ref<CategoryWithStubs[]>([]);
  const searchResults = ref<KnowledgeBaseItem[]>([]);
  const isSearching = ref<boolean>(false);
  let searchVersion = 0;
  const isVoting = new Set<string>();
  const isFetchingItem: Record<string, Promise<KnowledgeBaseItem> | false> =
    reactive({});

  const notifyStore = useNotifyStore();
  const { setNotifyError } = notifyStore;
  const router = useRouter();
  const $q = useQuasar();

  // Getters
  const list = computed(() => Array.from(byId.values()));
  const get = (uuid: string) => byId.get(uuid) ?? null;

  const fetchAllCategories = async (): Promise<KnowledgeBaseCategory[]> => {
    loading.value = true;
    error.value = null;
    try {
      const {
        data: { data },
      } = await apiService.get<{ data: KnowledgeBaseCategory[] }>(
        `/api/knowledge-base-categories/`,
      );
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon categorieën niet laden",
      });
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const createCategory = async (
    payload: KnowledgeBaseCategoryPayload,
  ): Promise<KnowledgeBaseCategory> => {
    error.value = null;
    try {
      const {
        data: { data },
      } = await apiService.post<{ data: KnowledgeBaseCategory }>(
        `/api/knowledge-base-categories`,
        payload,
      );
      // Append locally instead of refetching: the list endpoint filters out
      // categories without visible items, which would drop the fresh, empty one.
      categories.value.push({ ...data, items: [] });
      $q.notify({ color: "positive", message: "Categorie aangemaakt" });
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon categorie niet aanmaken",
      });
      throw e;
    }
  };

  const updateCategory = async (
    uuid: string,
    payload: KnowledgeBaseCategoryPayload,
  ): Promise<KnowledgeBaseCategory> => {
    error.value = null;
    try {
      const {
        data: { data },
      } = await apiService.put<{ data: KnowledgeBaseCategory }>(
        `/api/knowledge-base-categories/${uuid}`,
        payload,
      );
      const category = categories.value.find((c) => c.uuid === uuid);
      if (category) {
        category.title = data.title;
        category.module_ids = data.module_ids;
      }
      $q.notify({ color: "positive", message: "Categorie bijgewerkt" });
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon categorie niet bijwerken",
      });
      throw e;
    }
  };

  const deleteCategory = async (uuid: string): Promise<void> => {
    error.value = null;
    try {
      await apiService.delete(`/api/knowledge-base-categories/${uuid}`);
      const index = categories.value.findIndex((c) => c.uuid === uuid);
      if (index !== -1) {
        categories.value.splice(index, 1);
      }
      $q.notify({ color: "positive", message: "Categorie verwijderd" });
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon categorie niet verwijderen",
      });
      throw e;
    }
  };

  const fetchAdminTree = async (): Promise<KnowledgeBaseTreeApiCategory[]> => {
    const {
      data: { data },
    } = await apiService.get<{ data: KnowledgeBaseTreeApiCategory[] }>(
      `/api/knowledge-base-categories/tree`,
    );
    return data;
  };

  const saveLayout = async (
    payload: KnowledgeBaseLayoutPayloadNode[],
  ): Promise<void> => {
    error.value = null;
    loading.value = true;
    try {
      await apiService.post(`/api/knowledge-base-categories/sync-layout`, {
        categories: payload,
      });
      await fetchAll();
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon indeling niet opslaan",
      });
      throw e;
    } finally {
      loading.value = false;
    }
  };

  // Accepts slug for detail endpoint
  const fetchOne = async (slug: string): Promise<KnowledgeBaseItem> => {
    if (!slug) {
      throw new Error("Slug is required");
    }

    if (isFetchingItem[slug]) {
      return isFetchingItem[slug] as Promise<KnowledgeBaseItem>;
    }

    const local =
      Array.from(byId.values()).find((i) => i.slug === slug) || null;
    if (local) {
      activeItem.value = local;
      return local;
    }

    error.value = null;
    loading.value = true;

    isFetchingItem[slug] = (async () => {
      try {
        const {
          data: { data },
        } = await apiService.get<{ data: KnowledgeBaseItem }>(
          `/api/knowledge-base-items/${slug}`,
        );
        byId.set(data.uuid, data);
        activeItem.value = data;
        return data;
      } catch (e: unknown) {
        setNotifyError({
          error: e as object,
          fallbackMessage: "Kon kennisbank item niet laden",
        });
        throw e;
      } finally {
        isFetchingItem[slug] = false;
        loading.value = false;
      }
    })();

    return isFetchingItem[slug] as Promise<KnowledgeBaseItem>;
  };

  const vote = async (uuid: string, helpful: boolean): Promise<void> => {
    // A double-click would otherwise fire a second POST while the first is still
    // in flight: both carry the pre-vote cookie, so the backend counts both.
    if (isVoting.has(uuid)) {
      return;
    }
    isVoting.add(uuid);

    try {
      await apiService.post(`/api/knowledge-base/${uuid}/vote`, { helpful });
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon je stem niet opslaan",
      });
      return;
    } finally {
      isVoting.delete(uuid);
    }

    // The backend answers 204: patch the cached article so the widget shows the
    // new state without refetching.
    const item =
      byId.get(uuid) ??
      (activeItem.value?.uuid === uuid ? activeItem.value : null);
    if (item) {
      applyHelpfulnessVote(item, helpful);
    }
  };

  const fetchAll = async (): Promise<CategoryWithStubs[]> => {
    loading.value = true;
    error.value = null;
    try {
      const {
        data: { data },
      } = await apiService.get<{ data: CategoryWithStubs[] }>(
        `/api/knowledge-base`,
      );
      // fill categories + flat map for detail cache
      categories.value = data;
      return categories.value;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon kennisbank categorieën en items niet laden",
      });
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const create = async (
    payload: KnowledgeBaseItemCreate,
  ): Promise<KnowledgeBaseItem> => {
    error.value = null;

    try {
      const body: Record<string, unknown> = { ...payload } as Record<
        string,
        unknown
      >;
      const {
        data: { data },
      } = await apiService.post<{ data: KnowledgeBaseItem }>(
        `/api/knowledge-base-items`,
        body,
      );

      byId.set(data.uuid, data);
      activeItem.value = data;
      await fetchAll();

      $q.notify({ color: "positive", message: "Item opgeslagen" });

      router.push({ name: "knowledgeBaseItem", params: { slug: data.slug } });
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon item niet opslaan",
      });
      throw e;
    }
  };

  const update = async (
    payload: KnowledgeBaseItemUpdate,
  ): Promise<KnowledgeBaseItem> => {
    error.value = null;
    try {
      const prev = byId.get(payload.uuid) || null;
      const body: Record<string, unknown> = { ...payload } as Record<
        string,
        unknown
      >;
      const {
        data: { data },
      } = await apiService.put<{ data: KnowledgeBaseItem }>(
        `/api/knowledge-base-items/${payload.uuid}`,
        body,
      );
      byId.set(data.uuid, data);
      activeItem.value = data;
      await fetchAll();

      $q.notify({ color: "positive", message: "Item bijgewerkt" });

      // If slug changed, redirect to new slug
      const slugChanged = prev?.slug !== data.slug && Boolean(data.slug);
      if (slugChanged === true) {
        router.push({ name: "knowledgeBaseItem", params: { slug: data.slug } });
      }
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon item niet bijwerken",
      });
      throw e;
    }
  };

  const searchItems = async (term: string): Promise<void> => {
    const currentVersion = ++searchVersion;
    const trimmed = term.trim();
    if (!trimmed) {
      searchResults.value = [];
      isSearching.value = false;
      return;
    }
    isSearching.value = true;
    try {
      const {
        data: { data },
      } = await apiService.get<{ data: KnowledgeBaseItem[] }>(
        `/api/knowledge-base/search`,
        { params: { q: trimmed } },
      );
      if (currentVersion !== searchVersion) {
        return;
      }
      searchResults.value = data;
    } catch (e: unknown) {
      if (currentVersion !== searchVersion) {
        return;
      }
      searchResults.value = [];
      setNotifyError({
        error: e as object,
        fallbackMessage: "Zoeken mislukt",
      });
    } finally {
      if (currentVersion === searchVersion) {
        isSearching.value = false;
      }
    }
  };

  const clearSearch = (): void => {
    searchVersion++;
    searchResults.value = [];
    isSearching.value = false;
  };

  const remove = async (uuid: string): Promise<void> => {
    error.value = null;
    try {
      await apiService.delete(`/api/knowledge-base-items/${uuid}`);

      byId.delete(uuid);
      await fetchAll();

      if (activeItem.value?.uuid === uuid) {
        activeItem.value = null;
      }

      $q.notify({ color: "positive", message: "Item verwijderd" });

      router.push({ name: "knowledgeBaseIndex" });
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon item niet verwijderen",
      });
      throw e;
    }
  };

  return {
    // state
    byId,
    loading,
    error,
    activeItem,
    categories,
    searchResults,
    isSearching,
    // getters
    get,
    list,
    // actions
    fetchAllCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    fetchOne,
    vote,
    fetchAll,
    fetchAdminTree,
    saveLayout,
    create,
    update,
    remove,
    searchItems,
    clearSearch,
  };
});
